import { LabelledEntity } from 'shared/types/commonView';
import {
  ISO6391LanguageCode,
  ISO6391LanguageCodes,
  LANGUAGE_LABELS,
  LOCALE_LANGUAGES,
  LocaleCountryAndLanguageCode,
} from 'shared/types/locale';
import { anyToSupportedISO6391LanguageCode } from './anyToSupportedISO6391LanguageCode';

/**
 * ISO3166-1 alpha-2 for flag emoji — aligned with {@link LOCALE_LANGUAGES} region tags.
 * `en` uses GB as the conventional English report locale flag.
 */
const ISO6391_TO_FLAG_COUNTRY: Record<ISO6391LanguageCode, string> = {
  [ISO6391LanguageCode.en]: 'GB',
  [ISO6391LanguageCode.ar]: 'AE',
  [ISO6391LanguageCode.bg]: 'BG',
  [ISO6391LanguageCode.hr]: 'HR',
  [ISO6391LanguageCode.cs]: 'CZ',
  [ISO6391LanguageCode.fr]: 'FR',
  [ISO6391LanguageCode.de]: 'DE',
  [ISO6391LanguageCode.he]: 'IL',
  [ISO6391LanguageCode.hu]: 'HU',
  [ISO6391LanguageCode.nb]: 'NO',
  [ISO6391LanguageCode.pl]: 'PL',
  [ISO6391LanguageCode.pt]: 'PT',
  [ISO6391LanguageCode.ro]: 'RO',
  [ISO6391LanguageCode.ru]: 'UA',
  [ISO6391LanguageCode.sk]: 'SK',
  [ISO6391LanguageCode.es]: 'ES',
  [ISO6391LanguageCode.tr]: 'TR',
  [ISO6391LanguageCode.uk]: 'UA',
};

const LOCALE_TAG_TO_ISO6391 = Object.entries(LOCALE_LANGUAGES).reduce(
  (acc, [iso, tag]) => {
    acc[tag] = iso as ISO6391LanguageCode;
    return acc;
  },
  {} as Record<LocaleCountryAndLanguageCode, ISO6391LanguageCode>
);

export function countryCodeToFlagEmoji(countryCode: string): string {
  const code = countryCode.toUpperCase();
  if (code.length !== 2) {
    return '';
  }
  return [...code]
    .map((char) =>
      String.fromCodePoint(0x1f1e6 + char.charCodeAt(0) - 65)
    )
    .join('');
}

export function getReportLanguageShortCode(
  code: ISO6391LanguageCode
): string {
  return code.toUpperCase();
}

export function iso6391ToFlagEmoji(code: ISO6391LanguageCode): string {
  return countryCodeToFlagEmoji(ISO6391_TO_FLAG_COUNTRY[code]);
}

export function toReportLocaleOption(
  code: ISO6391LanguageCode
): LabelledEntity {
  return {
    _id: LOCALE_LANGUAGES[code],
    label: LANGUAGE_LABELS[code],
  };
}

export function localeOptionToIso6391(
  option: LabelledEntity
): ISO6391LanguageCode {
  const fromTag = LOCALE_TAG_TO_ISO6391[
    option._id as LocaleCountryAndLanguageCode
  ];
  if (fromTag) {
    return fromTag;
  }
  return anyToSupportedISO6391LanguageCode(option._id);
}

export const REPORT_LOCALE_OPTIONS: LabelledEntity[] = ISO6391LanguageCodes.map(
  (code) => toReportLocaleOption(code)
);

export function getDefaultReportLocaleOption(
  lastSavedLocaleTag: LocaleCountryAndLanguageCode | null
): LabelledEntity {
  if (lastSavedLocaleTag && LOCALE_TAG_TO_ISO6391[lastSavedLocaleTag]) {
    return toReportLocaleOption(LOCALE_TAG_TO_ISO6391[lastSavedLocaleTag]);
  }
  return toReportLocaleOption(ISO6391LanguageCode.pl);
}
